import { useEffect, useMemo, useState } from "react";
import { pageStatus, serviceLabel, PAGE_STATES, type Page, type PageState, type Picklists, type Search } from "@pagelink/shared";
import { api } from "./api.ts";
import { PageRow, collapseTeams } from "./PageList.tsx";
import { Icon, Note } from "./ui.tsx";

const SEP = "\u0001";
const STATE_LABEL: Record<PageState, string> = {
  sent: "Sent", delivered: "Delivered", seen: "Seen", accepted: "Answered",
  done: "Done", declined: "Declined", cancelled: "Cancelled", redirected: "Redirected",
};

/* "open" and "finished" are groups of states, filtered here; a single state
 * goes to the server with the rest of the search. */
type StatusFilter = "" | "open" | "finished" | PageState;

interface Filters {
  q: string; direction: "" | "in" | "out"; status: StatusFilter; urgency: "" | "STAT" | "Routine";
  target: string; consultTypeId: string; from: string; to: string; hasMessages: boolean;
}
const EMPTY: Filters = { q: "", direction: "", status: "finished", urgency: "", target: "", consultTypeId: "", from: "", to: "", hasMessages: false };

/* ---------------------------------------------------------------------------
 * History and search. Every page the viewer sent or received, searched on the
 * server, so it reaches past what the inbox holds. Free text matches people,
 * service, context and the discussion; the filters narrow by direction,
 * status, urgency, division or service, consult type and dates.
 * ------------------------------------------------------------------------ */
export function History({ meId, onOpen, stars, onStar }: {
  incoming: Page[]; outgoing: Page[]; meId: string; onOpen: (id: string) => void; stars: Set<string>; onStar: (id: string) => void;
}) {
  const [f, setF] = useState<Filters>(EMPTY);
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<Picklists | null>(null);
  const [pages, setPages] = useState<Page[] | null>(null);
  const [capped, setCapped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => { api.picklists().then(setLists).catch(() => {}); }, []);

  // Debounced, so typing does not fire a search per keystroke.
  useEffect(() => {
    const [service = "", subService = ""] = f.target.split(SEP);
    const q: Search = {
      q: f.q.trim() || undefined, direction: f.direction || undefined, urgency: f.urgency || undefined,
      state: f.status && f.status !== "open" && f.status !== "finished" ? f.status : undefined,
      service: service || undefined, subService: subService || undefined, consultTypeId: f.consultTypeId || undefined,
      from: f.from || undefined, to: f.to || undefined, hasMessages: f.hasMessages ? "yes" : undefined,
    };
    let live = true;
    const t = setTimeout(() => {
      api.search(q)
        .then((r) => { if (live) { setPages(r.pages); setCapped(r.capped); setError(null); } })
        .catch(() => live && setError("Search did not work. Try again."));
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [f]);

  const shown = (pages ?? []).filter((p) => {
    const r = pageStatus(p).resolved;
    return f.status === "open" ? !r : f.status === "finished" ? r : true;
  });

  const active = (Object.keys(EMPTY) as (keyof Filters)[]).filter((k) => k !== "q" && f[k] !== EMPTY[k]).length;

  const targets = useMemo(() => (lists?.services ?? []).flatMap((s) => [
    { value: `${s.name}${SEP}`, text: s.name, grp: s.grp },
    ...(lists?.subServices ?? []).filter((x) => x.division === s.name)
      .map((x) => ({ value: `${s.name}${SEP}${x.name}`, text: ` ${serviceLabel(s.name, x.name, s.short)}`, grp: s.grp })),
  ]), [lists]);
  const groups = Array.from(new Set(targets.map((t) => t.grp)));

  return (
    <>
      <h1 className="screen-title">History</h1>
      <p className="screen-sub">Search every page you sent or received.</p>

      <div className="search">
        <span className="ic"><Icon name="search" size={16} /></span>
        <input type="search" value={f.q} onChange={(e) => set("q", e.target.value)}
          placeholder="Name, service, context or message" aria-label="Search pages" />
      </div>

      <div className="filterbar">
        <button className="linkish" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? "Hide filters" : "Filters"}{active > 0 ? ` (${active})` : ""}
        </button>
        {active > 0 && <button className="linkish" onClick={() => setF({ ...EMPTY, q: f.q })}>Clear filters</button>}
        <span className="count">{pages ? `${shown.length} page${shown.length === 1 ? "" : "s"}` : ""}</span>
      </div>

      {open && (
        <div className="filters">
          <label>Direction
            <select value={f.direction} onChange={(e) => set("direction", e.target.value as Filters["direction"])}>
              <option value="">Sent and received</option>
              <option value="in">Received</option>
              <option value="out">Sent</option>
            </select>
          </label>
          <label>Status
            <select value={f.status} onChange={(e) => set("status", e.target.value as StatusFilter)}>
              <option value="">Any</option>
              <option value="open">Open</option>
              <option value="finished">Finished</option>
              <optgroup label="Exactly">
                {PAGE_STATES.map((s) => <option key={s} value={s}>{STATE_LABEL[s]}</option>)}
              </optgroup>
            </select>
          </label>
          <label>Urgency
            <select value={f.urgency} onChange={(e) => set("urgency", e.target.value as Filters["urgency"])}>
              <option value="">Any</option>
              <option value="STAT">STAT</option>
              <option value="Routine">Routine</option>
            </select>
          </label>
          <label>Consult type
            <select value={f.consultTypeId} onChange={(e) => set("consultTypeId", e.target.value)}>
              <option value="">Any</option>
              {(lists?.consultTypes ?? []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          <label className="wide">Division or service
            <select value={f.target} onChange={(e) => set("target", e.target.value)}>
              <option value="">Any</option>
              {groups.map((g) => (
                <optgroup key={g} label={g}>
                  {targets.filter((t) => t.grp === g).map((t) => <option key={t.value} value={t.value}>{t.text}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <label>From
            <input type="date" value={f.from} max={f.to || undefined} onChange={(e) => set("from", e.target.value)} />
          </label>
          <label>To
            <input type="date" value={f.to} min={f.from || undefined} onChange={(e) => set("to", e.target.value)} />
          </label>
          <label className="wide check">
            <input type="checkbox" checked={f.hasMessages} onChange={(e) => set("hasMessages", e.target.checked)} />
            <span>Only pages with a discussion</span>
          </label>
        </div>
      )}

      {error && <Note warn>{error}</Note>}
      {pages && shown.length === 0 && <Note>No pages match.</Note>}
      {collapseTeams(shown, meId).map(({ page: p, team }) => (
        <PageRow key={p.id} page={p} meId={meId} direction={p.toId === meId ? "in" : "out"} muted={pageStatus(p).resolved} team={team}
          onOpen={() => onOpen(p.id)} starred={stars.has(p.id)} onStar={() => onStar(p.id)} />
      ))}
      {capped && <div className="fine">Showing the newest 500. Narrow the search to see older pages.</div>}
    </>
  );
}
